import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import axios from '../../utils/axios';

interface IInitialState {
  summary: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: {
      KITCHEN: {
        'in-stock': number;
        'low-stock': number;
        'out-of-stock': number;
      };
    };
  };
  expiringSoon: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    totalQuantity: number;
  };
  expired: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    totalQuantity: number;
  };
}

const initialState: IInitialState = {
  summary: {
    status: 'idle',
    error: undefined,
    data: {
      KITCHEN: {
        'in-stock': 0,
        'low-stock': 0,
        'out-of-stock': 0,
      },
    },
  },
  expiringSoon: {
    status: 'idle',
    error: undefined,
    totalQuantity: 0,
  },
  expired: {
    status: 'idle',
    error: undefined,
    totalQuantity: 0,
  },
};

const inventoryAnalysisSlice = createSlice({
  name: 'inventoryAnalysis',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getInventoryAnalysis.pending, (state) => {
        state.summary.status = 'loading';
      })
      .addCase(getInventoryAnalysis.fulfilled, (state, action) => {
        state.summary.status = 'success';
        state.summary.data = action.payload;
      })
      .addCase(getInventoryAnalysis.rejected, (state, action) => {
        state.summary.status = 'failure';
        state.summary.error = action.error.message;
      })

      .addCase(getExpiringSoonSummary.pending, (state) => {
        state.expiringSoon.status = 'loading';
      })
      .addCase(getExpiringSoonSummary.fulfilled, (state, action) => {
        state.expiringSoon.status = 'success';
        state.expiringSoon.totalQuantity = action.payload.totalQuantity;
      })
      .addCase(getExpiringSoonSummary.rejected, (state, action) => {
        state.expiringSoon.status = 'failure';
        state.expiringSoon.error = action.error.message;
      })

      .addCase(getExpiredSummary.pending, (state) => {
        state.expired.status = 'loading';
      })
      .addCase(getExpiredSummary.fulfilled, (state, action) => {
        state.expired.status = 'success';
        state.expired.totalQuantity = action.payload.totalQuantity;
      })
      .addCase(getExpiredSummary.rejected, (state, action) => {
        state.expired.status = 'failure';
        state.expired.error = action.error.message;
      });
  },
});

export default inventoryAnalysisSlice.reducer;

export const getInventoryAnalysis = createAsyncThunk('getInventoryAnalysis', async () => {
  try {
    const response = await axios.get(`inventory/item/availability`);

    if (Object.keys(response.data.data).length === 0) {
      return initialState.summary.data;
    }

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getExpiringSoonSummary = createAsyncThunk('getExpiringSoonSummary', async () => {
  try {
    const response = await axios.get(`inventory/batches/analytics/expiring-soon`);

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getExpiredSummary = createAsyncThunk('getExpiredSummary', async () => {
  try {
    const response = await axios.get(`inventory/batches/analytics/expired`);

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});
